import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { invoiceManagementInvoiceValidationSchema } from '../../utils/validationSchemas';
import { InvoiceManagementInvoiceFormType } from '../forms/invoice/types';
import { InvoiceManagementConnectedInvoiceForm } from './InvoiceManagementConnectedInvoiceForm';

export type InvoiceManagementInvoiceFormProviderProps = {
  readonly?: boolean;
  onSubmit: (data: InvoiceManagementInvoiceFormType) => void;
  defaultValues?: Partial<InvoiceManagementInvoiceFormType>;
  defaultValidated?: boolean;
  formId?: string;
};

export const InvoiceManagementInvoiceFormProvider = ({
  readonly,
  onSubmit,
  defaultValues = {},
  defaultValidated,
  formId,
}: InvoiceManagementInvoiceFormProviderProps) => {
  const { trigger, reset, control, ...formMethods } = useForm({
    defaultValues: { ...defaultEmptyValues, ...defaultValues },
    resolver: yupResolver(invoiceManagementInvoiceValidationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // used to trigger validation on startup
  useEffect(() => {
    if (defaultValidated) {
      trigger();
    }
  }, [defaultValidated, trigger]);

  // used to update data in readonly mode
  useEffect(() => {
    if (readonly) {
      reset({ ...defaultEmptyValues, ...defaultValues });
    }
  }, [readonly, defaultValues, reset]);

  return (
    <FormProvider {...formMethods} trigger={trigger} reset={reset} control={control}>
      <InvoiceManagementConnectedInvoiceForm onSubmit={onSubmit} readonly={readonly} formId={formId} />
    </FormProvider>
  );
};

const defaultEmptyValues: InvoiceManagementInvoiceFormType = {
  number: '',
  amount: null,
  amountWithoutVat: null,
  amountVat: null,
  taxPayer: false,
  currency: 'czk',
  issueDate: '',
  dueDate: '',
  taxPoint: '',
  bankAccount: '',
  bankNumber: '',
  iban: '',
  swift: '',
  variableSymbol: '',
  customer: {
    name: '',
    address: '',
    email: '',
    phoneNumber: '',
    taxId: '',
    vatId: '',
  },
  vendor: {
    name: '',
    address: '',
    taxId: '',
    vatId: '',
  },
  items: [
    {
      name: '',
      pricePerUnit: '',
      priceSum: '',
      quantity: 1,
      vatRate: '',
      priceWithoutVat: '',
      unit: 'ks',
    },
  ],
};
